import { Link } from "react-router";
import { Avatar } from "~/components/Avatar";
import { Text } from "~/components/Text";
import { useRootLocale } from "~/root";
import { formatEventDateTime } from "~/utils/date";

type UpcomingEvent = {
  id: string;
  title: string;
  startDate: string | Date;
  bannerImageUrl?: string | null;
  location: {
    suburb?: string | null;
    city?: string | null;
    state?: string | null;
  };
  hosts: Array<{
    primary: boolean;
    user: {
      id: string;
      name: string;
      profileImageUrl: string;
    };
  }>;
};

type UpcomingEventCardProps = {
  event: UpcomingEvent;
};

export function UpcomingEventCard({ event }: UpcomingEventCardProps) {
  const locale = useRootLocale();
  const primaryHost = event.hosts.find((h) => h.primary)?.user;
  const dateLabel = formatEventDateTime(event.startDate, locale);
  const locationLabel = [event.location.suburb, event.location.state]
    .filter((part): part is string => Boolean(part))
    .join(", ");

  return (
    <Link to={`/plans/${event.id}`} className="group block cursor-pointer">
      <div className="relative overflow-hidden rounded-lg">
        <div
          className="w-full aspect-[434/372] bg-butter-cream bg-cover bg-center transition-transform duration-300 group-hover:scale-[1.03]"
          style={{
            backgroundImage: `url(${
              event.bannerImageUrl
                ? event.bannerImageUrl
                : "/images/default-plan-banner.png"
            })`,
          }}
        />
        <span className="absolute top-2 left-2 inline-flex items-center rounded-md bg-butter-cream-light px-2.5 py-1 shadow-sm">
          <Text as="span" variant="secondary" size="xs">
            {dateLabel}
          </Text>
        </span>
      </div>
      <div className="pt-2">
        <div className="mb-2 flex items-center gap-2">
          {primaryHost && (
            <Avatar user={primaryHost} size={28} avatarTextSize="xs" />
          )}
          <Text variant="secondary" size="sm">
            Hosted by {primaryHost?.name ?? "Unknown"}
          </Text>
        </div>
        <Text variant="primary" size="sm" as="h3" className="mb-1 line-clamp-1 !font-sans">
          {event.title}
        </Text>
        <Text variant="secondary" size="sm" className="line-clamp-1">
          {locationLabel || event.location.city || ""}
        </Text>
      </div>
    </Link>
  );
}
